import React, { FC, useState, useEffect, useCallback, useMemo, SyntheticEvent } from 'react'
import { useFormContext } from 'react-hook-form'
import { InputLabel, Autocomplete, FormHelperText, Grid, TextField } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'
import { useRest } from '../Context'
import { SelectArrow } from '../SelectArrow'
import { useCountriesList } from './useCountriesList/useCountriesList'
import capitalizeFirstLetter from '../../utils/capitalizeFirstLetter'
import { errorMessage as errorText } from '../../constants/errorMessages'

const useStyles = makeStyles((theme: Theme) => ({
  inputContainer: {
    width: ({ width }: CountrySelectProps) => width,
    marginTop: ({ mt }: CountrySelectProps) => mt,
    marginBottom: ({ mb }: CountrySelectProps) => mb,
    marginLeft: ({ ml }: CountrySelectProps) => ml,
    marginRight: ({ mr }: CountrySelectProps) => mr,
    paddingLeft: ({ pl }: CountrySelectProps) => pl,
    height: 63,
    '& .MuiAutocomplete-clearIndicator': {
      color: theme.palette.primary.light,
      opacity: 0.6,
      fontSize: '18px',
    },
    '& #select-outlined': {
      color: 'rgba(255,255,255,0.6)',
      fontSize: 14,
    },
    '& .MuiOutlinedInput-input:-webkit-autofill': {
      '-webkit-box-shadow': 'none',
    },
    '& :-webkit-autofill': {
      '-webkit-background-clip': 'text',
    },
  },
  label: {
    fontSize: '18px',
    marginBottom: '-3px',
    color: '#FFFFFF',
    opacity: '60%',
  },
  selectField: {
    color: '#FFFFFF',
  },
  noOptions: {
    color: theme.palette.primary.light,
  },
}))

interface CountrySelectProps {
  inputName: string
  labelName?: string
  placeholder?: string
  width: number | string
  mt?: number | string
  mb?: number | string
  ml?: number | string
  mr?: number | string
  pl?: number | string
}

const defaultProps = {
  inputName: 'country',
}

const CountrySelect: FC<CountrySelectProps> = ({ inputName, labelName, placeholder, width, pl, mb, ml, mr, mt }) => {
  const [blackListCountry, setBlackListCountry] = useState([])
  const [country, setCountry] = useState('')
  const [isSelected, setIsSelected] = useState<boolean>(false)
  const [isForbiddenCountry, setIsForbiddenCountry] = useState<boolean>(false)
  // eslint-disable-next-line
  const [error, setErrors] = useState<any>('')
  const options = useCountriesList()
  const styles = useStyles({ width, mt, mb, pl, placeholder, ml, mr, inputName })
  const { rxios } = useRest()
  const {
    register,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext()

  useEffect(() => {
    register(inputName, { required: true })
  }, [register, inputName])

  useEffect(() => {
    const getCountriesBlacklist = async () => {
      // eslint-disable-next-line
      rxios.get('country-blacklist').subscribe(
        // eslint-disable-next-line
        (next: any): void => {
          setBlackListCountry(next)
        },
        (err) => {
          // eslint-disable-next-line
          console.log(err)
        },
      )
    }
    getCountriesBlacklist()
  }, [rxios])

  useEffect(() => {
    const result = country?.length && isForbiddenCountry ? true : !country?.length
    setIsSelected(result)
  }, [country, isForbiddenCountry])

  useEffect(() => {
    setErrors(errors[inputName])
  }, [errors, inputName])

  const changeHandler = useCallback(
    // eslint-disable-next-line
    (valueSelect: any) => {
      // eslint-disable-next-line
      const result = blackListCountry.find((el: any) => el.countryKey === valueSelect?.value)
      setIsForbiddenCountry(!!result)
      setValue(inputName, result ? '' : valueSelect?.label, {
        shouldValidate: true,
        shouldDirty: true,
      })
      setCountry(valueSelect?.label)
      if (result) {
        setErrors({ message: errorText.EMPTY_FIELD })
      }
    },
    [blackListCountry, inputName, setValue],
  )

  const errorMessage = useMemo(() => {
    if (!error) return false
    const message = error?.message.replace(inputName, '')
    return capitalizeFirstLetter(message)
  }, [error, inputName])

  const message = useMemo(() => {
    let text = errorMessage
    if (isForbiddenCountry) {
      text = errorText.FORBIDDEN_COUNTRY
    }
    return isSelected ? text : ''
  }, [isForbiddenCountry, errorMessage, isSelected])

  return (
    <Grid container item className={styles.inputContainer} direction={'column'} wrap={'nowrap'}>
      {labelName && (
        <Grid item>
          <InputLabel shrink htmlFor={inputName} className={styles.label}>
            {labelName}
          </InputLabel>
        </Grid>
      )}
      <Grid item>
        <Autocomplete
          id={inputName}
          options={options}
          size="small"
          className={styles.selectField}
          onChange={(event: SyntheticEvent, newValue: { label: string; value: string } | null) => {
            event.preventDefault()
            changeHandler(newValue)
          }}
          noOptionsText="Country not found"
          classes={{
            noOptions: styles.noOptions,
          }}
          popupIcon={<SelectArrow />}
          getOptionLabel={(option) => option.label}
          style={{ width: 300 }}
          value={options?.find((option) => option.label === getValues(inputName)) || null}
          renderInput={(params) => (
            <TextField {...params} placeholder={placeholder} error={!!error && isSelected} variant="outlined" />
          )}
        />
      </Grid>
      <Grid item>
        {error && (
          <FormHelperText error id={`component-error-text-${inputName}`}>
            {message}
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  )
}

CountrySelect.defaultProps = defaultProps

export default CountrySelect
